<template>
    <div class="bg-transparent text-white text-xs space-y-2 min-h-full">

        <!-- CONNECTIONS -->
        <Accordion v-if="appSettings.tauri" title="Connections"
            class="pl-2 pr-2 space-y-2 bg-white/15 border border-white/20 rounded-md">

            <!-- MQTT -->
            <Accordion title="MQTT" class="space-y-2 border border-white/20 rounded-md">
                <div class="flex items-center gap-2 py-0.5">
                    <span class="w-2/5 shrink-0 text-xs text-gray-400">Protocol</span>
                    <select v-model="appSettings.mqtt.protocol" @input="$emit('saveSettings', 'mqtt')"
                        class="flex-1 bg-white/15 border border-white/20 rounded px-2 py-1 text-white text-xs focus:outline-none focus:border-slate-400">
                        <option value="mqtt">mqtt</option>
                        <option value="mqtts">mqtts</option>
                        <option value="ws">ws</option>
                        <option value="wss">wss</option>
                    </select>
                </div>
                <div class="flex items-center gap-2 py-0.5">
                    <span class="w-2/5 shrink-0 text-xs text-gray-400">Host</span>
                    <input type="text" v-model="appSettings.mqtt.host" placeholder="mqts://192.168.1.10:9001"
                        @input="$emit('saveSettings', 'mqtt')"
                        class="flex-1 bg-white/15 border border-white/20 rounded px-2 py-1 text-white text-xs focus:outline-none focus:border-slate-400 placeholder-gray-600" />
                </div>
                <div class="flex items-center gap-2 py-0.5">
                    <span class="w-2/5 shrink-0 text-xs text-gray-400">Port</span>
                    <input type="number" v-model="appSettings.mqtt.port" placeholder="1883"
                        @input="$emit('saveSettings', 'mqtt')"
                        class="flex-1 bg-white/15 border border-white/20 rounded px-2 py-1 text-white text-xs focus:outline-none focus:border-slate-400 placeholder-gray-600" />
                </div>
                <div class="flex items-center gap-2 py-0.5">
                    <span class="w-2/5 shrink-0 text-xs text-gray-400">MQTT id</span>
                    <input type="text" v-model="appSettings.mqtt.id" placeholder="haspdesigner"
                        @input="$emit('saveSettings', 'mqtt')"
                        class="flex-1 bg-white/15 border border-white/20 rounded px-2 py-1 text-white text-xs focus:outline-none focus:border-slate-400 placeholder-gray-600" />
                </div>
                <div class="flex items-center gap-2 py-0.5">
                    <span class="w-2/5 shrink-0 text-xs text-gray-400">Username</span>
                    <input type="text" v-model="appSettings.mqtt.username" placeholder="username"
                        @input="$emit('saveSettings', 'mqtt')"
                        class="flex-1 bg-white/15 border border-white/20 rounded px-2 py-1 text-white text-xs focus:outline-none focus:border-slate-400 placeholder-gray-600" />
                </div>
                <div class="flex items-center gap-2 py-0.5">
                    <span class="w-2/5 shrink-0 text-xs text-gray-400">Password</span>
                    <input type="password" v-model="appSettings.mqtt.password" placeholder="password"
                        @input="$emit('saveSettings', 'mqtt')"
                        class="flex-1 bg-white/15 border border-white/20 rounded px-2 py-1 text-white text-xs focus:outline-none focus:border-slate-400 placeholder-gray-600" />
                </div>
                <button class="w-full px-3 py-1 rounded text-xs transition-colors"
                    :class="{
                        'bg-slate-600 hover:bg-slate-500 text-white': !mqttTestStatus,
                        'bg-green-600 text-white': mqttTestStatus === 'success',
                        'bg-red-600 text-white': mqttTestStatus === 'error'
                    }"
                    :disabled="testingMqtt"
                    @click="$emit('testMqttConnection')">
                    <span v-if="testingMqtt">Testing...</span>
                    <span v-else>Test Connection</span>
                </button>
                <div v-if="mqttTestMessage" class="text-xs"
                    :class="{
                        'text-green-400': mqttTestStatus === 'success',
                        'text-red-400': mqttTestStatus === 'error'
                    }">
                    {{ mqttTestMessage }}
                </div>
            </Accordion>

            <!-- HOMEASSISTANT -->
            <Accordion title="Homeassistant" class="space-y-2 border border-white/20 rounded-md">

                <!-- HA API -->
                <Accordion title="API" class="space-y-2 border border-white/20 rounded-md">
                    <div class="flex items-center gap-2 py-0.5">
                        <span class="w-2/5 shrink-0 text-xs text-gray-400">Protocol</span>
                        <select v-model="appSettings.hass.protocol" @input="$emit('saveSettings', 'hass')"
                            class="flex-1 bg-white/15 border border-white/20 rounded px-2 py-1 text-white text-xs focus:outline-none focus:border-slate-400">
                            <option value="http">http</option>
                            <option value="https">https</option>
                        </select>
                    </div>
                    <div class="flex items-center gap-2 py-0.5">
                        <span class="w-2/5 shrink-0 text-xs text-gray-400">Host</span>
                        <input type="text" v-model="appSettings.hass.host" placeholder="homeassistant.local"
                            @input="$emit('saveSettings', 'hass')"
                            class="flex-1 bg-white/15 border border-white/20 rounded px-2 py-1 text-white text-xs focus:outline-none focus:border-slate-400 placeholder-gray-600" />
                    </div>
                    <div class="flex items-center gap-2 py-0.5">
                        <span class="w-2/5 shrink-0 text-xs text-gray-400">Port</span>
                        <input type="number" v-model="appSettings.hass.port" placeholder="443"
                            @input="$emit('saveSettings', 'hass')"
                            class="flex-1 bg-white/15 border border-white/20 rounded px-2 py-1 text-white text-xs focus:outline-none focus:border-slate-400 placeholder-gray-600" />
                    </div>
                    <div class="flex items-center gap-2 py-0.5">
                        <span class="w-2/5 shrink-0 text-xs text-gray-400">Token</span>
                        <input type="password" v-model="appSettings.hass.token"
                            placeholder="your_long_live_token_for_homeassistant"
                            @input="$emit('saveSettings', 'hass')"
                            class="flex-1 bg-white/15 border border-white/20 rounded px-2 py-1 text-white text-xs focus:outline-none focus:border-slate-400 placeholder-gray-600" />
                    </div>
                    <button class="w-full px-3 py-1 rounded text-xs transition-colors"
                        :class="{
                            'bg-slate-600 hover:bg-slate-500 text-white': !hassTestStatus,
                            'bg-green-600 text-white': hassTestStatus === 'success',
                            'bg-red-600 text-white': hassTestStatus === 'error'
                        }"
                        :disabled="testingHass"
                        @click="$emit('testHassConnection')">
                        <span v-if="testingHass">Testing...</span>
                        <span v-else>Test Connection</span>
                    </button>
                    <div v-if="hassTestMessage" class="text-xs"
                        :class="{
                            'text-green-400': hassTestStatus === 'success',
                            'text-red-400': hassTestStatus === 'error'
                        }">
                        {{ hassTestMessage }}
                    </div>
                </Accordion>

                <!-- SFTP deploy -->
                <Accordion title="SFTP deploy" class="space-y-2 border border-white/20 rounded-md">
                    <div class="flex items-center gap-2 py-0.5">
                        <span class="w-2/5 shrink-0 text-xs text-gray-400">SFTP port</span>
                        <input type="number" v-model="appSettings.hass.sftp_port" placeholder="22"
                            @input="$emit('saveSettings', 'hass')"
                            class="flex-1 bg-white/15 border border-white/20 rounded px-2 py-1 text-white text-xs focus:outline-none focus:border-slate-400 placeholder-gray-600" />
                    </div>
                    <div class="flex items-center gap-2 py-0.5">
                        <span class="w-2/5 shrink-0 text-xs text-gray-400">SFTP user</span>
                        <input type="text" v-model="appSettings.hass.sftp_user" placeholder="root"
                            @input="$emit('saveSettings', 'hass')"
                            class="flex-1 bg-white/15 border border-white/20 rounded px-2 py-1 text-white text-xs focus:outline-none focus:border-slate-400 placeholder-gray-600" />
                    </div>
                    <div class="flex items-center gap-2 py-0.5">
                        <span class="w-2/5 shrink-0 text-xs text-gray-400">SFTP pass</span>
                        <input type="password" v-model="appSettings.hass.sftp_pass" placeholder="password"
                            @input="$emit('saveSettings', 'hass')"
                            class="flex-1 bg-white/15 border border-white/20 rounded px-2 py-1 text-white text-xs focus:outline-none focus:border-slate-400 placeholder-gray-600" />
                    </div>
                    <div class="flex items-center gap-2 py-0.5">
                        <span class="w-2/5 shrink-0 text-xs text-gray-400">SFTP path</span>
                        <input type="text" v-model="appSettings.hass.sftp_path" placeholder="/config/"
                            @input="$emit('saveSettings', 'hass')"
                            class="flex-1 bg-white/15 border border-white/20 rounded px-2 py-1 text-white text-xs focus:outline-none focus:border-slate-400 placeholder-gray-600" />
                    </div>
                    <button class="w-full px-3 py-1 rounded text-xs transition-colors"
                        :class="{
                            'bg-slate-600 hover:bg-slate-500 text-white': !sftpTestStatus,
                            'bg-green-600 text-white': sftpTestStatus === 'success',
                            'bg-red-600 text-white': sftpTestStatus === 'error'
                        }"
                        :disabled="testingSftp"
                        @click="$emit('testSftpConnection')">
                        <span v-if="testingSftp">Testing...</span>
                        <span v-else>Test Connection</span>
                    </button>
                    <div v-if="sftpTestMessage" class="text-xs"
                        :class="{
                            'text-green-400': sftpTestStatus === 'success',
                            'text-red-400': sftpTestStatus === 'error'
                        }">
                        {{ sftpTestMessage }}
                    </div>
                </Accordion>
            </Accordion>
        </Accordion>

        <!-- UI SETTINGS -->
        <Accordion title="UI settings" class="pl-2 pr-2 space-y-2 bg-white/15 border border-white/20 rounded-md">

            <div class="border-t border-white/10 pt-2 mt-1 space-y-1">
                <p class="text-xs font-semibold text-gray-300">Connection defaults</p>
                <div class="flex items-center gap-2 py-0.5">
                    <span class="w-2/5 shrink-0 text-xs text-gray-400">Spacing</span>
                    <input type="number" min="1" max="100" step="1"
                        v-model.number="appSettings.ui.connection_spacing"
                        @input="$emit('updateConnectionSpacing')"
                        class="flex-1 bg-white/15 border border-white/20 rounded px-2 py-1 text-white text-xs focus:outline-none focus:border-slate-400" />
                    <span class="text-xs text-gray-500">px</span>
                </div>
                <div class="flex items-center gap-2 py-0.5">
                    <span class="w-2/5 shrink-0 text-xs text-gray-400">Line width</span>
                    <input type="number" min="1" max="100" step="1"
                        v-model.number="appSettings.ui.object_defaults.connection.line_width"
                        @input="$emit('saveSettings', 'ui')"
                        class="flex-1 bg-white/15 border border-white/20 rounded px-2 py-1 text-white text-xs focus:outline-none focus:border-slate-400" />
                    <span class="text-xs text-gray-500">px</span>
                </div>
                <div class="flex items-center gap-2 py-0.5">
                    <span class="w-2/5 shrink-0 text-xs text-gray-400">Corner radius</span>
                    <input type="number" min="0" max="100" step="1"
                        v-model.number="appSettings.ui.object_defaults.connection.corner_radius"
                        @input="$emit('saveSettings', 'ui')"
                        class="flex-1 bg-white/15 border border-white/20 rounded px-2 py-1 text-white text-xs focus:outline-none focus:border-slate-400" />
                    <span class="text-xs text-gray-500">px</span>
                </div>
                <p class="pt-1 text-xs font-semibold text-gray-400">Animation</p>
                <div class="flex items-center gap-2 py-0.5">
                    <span class="w-2/5 shrink-0 text-xs text-gray-400">Maximum speed</span>
                    <input type="number" min="1" step="1" v-model.number="appSettings.ui.connection_max_speed"
                        @input="$emit('saveSettings', 'ui')"
                        class="flex-1 bg-white/15 border border-white/20 rounded px-2 py-1 text-white text-xs focus:outline-none focus:border-slate-400" />
                    <span class="text-xs text-gray-500">px/s</span>
                </div>
                <div class="flex items-center gap-2 py-0.5">
                    <span class="w-2/5 shrink-0 text-xs text-gray-400">Enabled</span>
                    <input type="checkbox" v-model="appSettings.ui.object_defaults.connection.animation_enabled"
                        @change="$emit('saveSettings', 'ui')" />
                </div>
                <div class="flex items-center gap-2 py-0.5">
                    <span class="w-2/5 shrink-0 text-xs text-gray-400">Bullet size</span>
                    <input type="number" min="1" step="1"
                        v-model.number="appSettings.ui.object_defaults.connection.bullet_size"
                        @input="$emit('saveSettings', 'ui')"
                        class="flex-1 bg-white/15 border border-white/20 rounded px-2 py-1 text-white text-xs focus:outline-none focus:border-slate-400" />
                    <span class="text-xs text-gray-500">px</span>
                </div>
                <div class="flex items-center gap-2 py-0.5">
                    <span class="w-2/5 shrink-0 text-xs text-gray-400">Bullet count</span>
                    <input type="number" min="1" step="1"
                        v-model.number="appSettings.ui.object_defaults.connection.bullet_count"
                        @input="$emit('saveSettings', 'ui')"
                        class="flex-1 bg-white/15 border border-white/20 rounded px-2 py-1 text-white text-xs focus:outline-none focus:border-slate-400" />
                </div>
                <p v-if="appSettings.ui.object_defaults.connection.bullet_size >= appSettings.ui.connection_spacing"
                    class="text-xs text-orange-300">Bullet size must be smaller than connection spacing.</p>
            </div>

            <div class="flex items-center gap-2 py-0.5">
                <span class="w-2/5 shrink-0 text-xs text-gray-400">Zoom sensitivity</span>
                <input type="range" min="0.5" max="5" step="0.5" v-model="appSettings.ui.zoom_sensitivity"
                    @input="$emit('updateZoomSensitivity')"
                    class="flex-1 accent-cyan-500 cursor-pointer" />
            </div>
            <div class="flex items-center gap-2 py-0.5">
                <span class="w-2/5 shrink-0 text-xs text-gray-400">Ruler interval</span>
                <select v-model.number="appSettings.ui.ruler_scale"
                    @change="$emit('saveSettings', 'ui')"
                    class="flex-1 bg-white/15 border border-white/20 rounded px-2 py-1 text-white text-xs focus:outline-none focus:border-slate-400">
                    <option :value="10">10 px</option>
                    <option :value="5">5 px</option>
                </select>
            </div>

            <!-- Colors -->
            <div class="flex gap-2 pt-1">
                <div class="flex-1">
                    <span class="block text-xs text-gray-400 mb-1">UI color</span>
                    <input type="color" v-model="appSettings.ui.main_color"
                        @input="$emit('saveSettings', 'ui')"
                        class="w-full h-8 bg-white/15 border border-white/20 rounded px-1 py-0.5 cursor-pointer" />
                </div>
                <div class="flex-1">
                    <span class="block text-xs text-gray-400 mb-1">Canvas color</span>
                    <input type="color" v-model="appSettings.ui.canvas_color"
                        @input="$emit('saveSettings', 'ui')"
                        class="w-full h-8 bg-white/15 border border-white/20 rounded px-1 py-0.5 cursor-pointer" />
                </div>
            </div>

            <!-- Toggles -->
            <div class="space-y-1 pt-1">
                <label class="flex items-center gap-2 py-0.5 cursor-pointer">
                    <input type="checkbox" v-model="appSettings.ui.page0_overlay"
                        @change="$emit('saveSettings', 'ui')"
                        class="accent-cyan-500 w-3.5 h-3.5 cursor-pointer" />
                    <span class="text-xs text-gray-400">Page 0 overlay</span>
                </label>
                <label class="flex items-center gap-2 py-0.5 cursor-pointer">
                    <input type="checkbox" v-model="appSettings.ui.show_hasp_id"
                        @change="$emit('saveSettings', 'ui')"
                        class="accent-cyan-500 w-3.5 h-3.5 cursor-pointer" />
                    <span class="text-xs text-gray-400">Object IDs</span>
                </label>
                <label class="flex items-center gap-2 py-0.5 cursor-pointer">
                    <input type="checkbox" v-model="appSettings.ui.show_object_details"
                        @change="$emit('saveSettings', 'ui')"
                        class="accent-cyan-500 w-3.5 h-3.5 cursor-pointer" />
                    <span class="text-xs text-gray-400">Object details</span>
                </label>
                <label class="flex items-center gap-2 py-0.5 cursor-pointer">
                    <input type="checkbox" v-model="appSettings.ui.show_doc_icon"
                        @change="$emit('saveSettings', 'ui')"
                        class="accent-cyan-500 w-3.5 h-3.5 cursor-pointer" />
                    <span class="text-xs text-gray-400">Dock icons</span>
                </label>
                <label class="flex items-center gap-2 py-0.5 cursor-pointer">
                    <input type="checkbox" v-model="appSettings.ui.enable_history"
                        @change="$emit('saveSettings', 'ui')"
                        class="accent-cyan-500 w-3.5 h-3.5 cursor-pointer" />
                    <span class="text-xs text-gray-400">Enable history (undo/redo)</span>
                </label>
                <label class="flex items-center gap-2 py-0.5 cursor-pointer">
                    <input type="checkbox" v-model="appSettings.ui.sticky_guidelines"
                        @change="$emit('saveSettings', 'ui')"
                        class="accent-cyan-500 w-3.5 h-3.5 cursor-pointer" />
                    <span class="text-xs text-gray-400">Sticky guidelines</span>
                </label>
            </div>
        </Accordion>

        <!-- GITHUB SYNC SETTINGS -->
        <Accordion title="GitHub Sync" class="pl-2 pr-2 space-y-2 bg-white/15 border border-white/20 rounded-md">
            <p class="text-xs text-gray-500">
                Push and pull design files to/from a GitHub repository.
                Create a
                <a href="https://github.com/settings/tokens/new?scopes=repo&description=OpenHASP+Designer"
                   target="_blank" rel="noopener"
                   class="text-ui-active underline hover:text-white">Personal Access Token</a>
                with <span class="font-mono">repo</span> scope.
            </p>
            <div class="flex items-center gap-2 py-0.5">
                <span class="w-2/5 shrink-0 text-xs text-gray-400">Token (PAT)</span>
                <input type="password" v-model="appSettings.github.token" placeholder="ghp_…"
                    @input="$emit('saveSettings', 'github')"
                    class="flex-1 bg-white/15 border border-white/20 rounded px-2 py-1 text-white text-xs focus:outline-none focus:border-slate-400 placeholder-gray-600" />
            </div>
            <div class="flex items-center gap-2 py-0.5">
                <span class="w-2/5 shrink-0 text-xs text-gray-400">Owner</span>
                <input type="text" v-model="appSettings.github.owner" placeholder="your-username"
                    @input="$emit('saveSettings', 'github')"
                    class="flex-1 bg-white/15 border border-white/20 rounded px-2 py-1 text-white text-xs focus:outline-none focus:border-slate-400 placeholder-gray-600" />
            </div>
            <div class="flex items-center gap-2 py-0.5">
                <span class="w-2/5 shrink-0 text-xs text-gray-400">Repository</span>
                <input type="text" v-model="appSettings.github.repo" placeholder="my-hasp-designs"
                    @input="$emit('saveSettings', 'github')"
                    class="flex-1 bg-white/15 border border-white/20 rounded px-2 py-1 text-white text-xs focus:outline-none focus:border-slate-400 placeholder-gray-600" />
            </div>
            <div class="flex items-center gap-2 py-0.5">
                <span class="w-2/5 shrink-0 text-xs text-gray-400">Branch</span>
                <input type="text" v-model="appSettings.github.branch" placeholder="main"
                    @input="$emit('saveSettings', 'github')"
                    class="flex-1 bg-white/15 border border-white/20 rounded px-2 py-1 text-white text-xs focus:outline-none focus:border-slate-400 placeholder-gray-600" />
            </div>
            <div class="flex items-center gap-2 py-0.5">
                <span class="w-2/5 shrink-0 text-xs text-gray-400">Folder</span>
                <input type="text" v-model="appSettings.github.folder" placeholder="designs"
                    @input="$emit('saveSettings', 'github')"
                    class="flex-1 bg-white/15 border border-white/20 rounded px-2 py-1 text-white text-xs focus:outline-none focus:border-slate-400 placeholder-gray-600" />
            </div>
            <button class="w-full px-3 py-1 rounded text-xs transition-colors"
                :class="{
                    'bg-slate-600 hover:bg-slate-500 text-white': !githubTestStatus,
                    'bg-green-600 text-white': githubTestStatus === 'success',
                    'bg-red-600 text-white': githubTestStatus === 'error'
                }"
                :disabled="testingGithub"
                @click="$emit('testGithubConnection')">
                <span v-if="testingGithub">Testing…</span>
                <span v-else>Test Connection</span>
            </button>
            <div v-if="githubTestMessage" class="text-xs"
                :class="{
                    'text-green-400': githubTestStatus === 'success',
                    'text-red-400': githubTestStatus === 'error'
                }">
                {{ githubTestMessage }}
            </div>
        </Accordion>

        <input class="hidden" @change="handleLoadSettingsFromFile" ref="settingsfile" type="file" accept=".json" />
    </div>
</template>

<script>
import Accordion from './Accordion.vue';

export default {
    name: 'AppSettings',
    components: {
        Accordion,
    },
    props: {
        appSettings: {
            type: Object,
            required: true,
        },
        testingMqtt: {
            type: Boolean,
            default: false,
        },
        mqttTestStatus: {
            type: String,
            default: null,
        },
        mqttTestMessage: {
            type: String,
            default: '',
        },
        testingHass: {
            type: Boolean,
            default: false,
        },
        hassTestStatus: {
            type: String,
            default: null,
        },
        hassTestMessage: {
            type: String,
            default: '',
        },
        testingSftp: {
            type: Boolean,
            default: false,
        },
        sftpTestStatus: {
            type: String,
            default: null,
        },
        sftpTestMessage: {
            type: String,
            default: '',
        },
        testingGithub: {
            type: Boolean,
            default: false,
        },
        githubTestStatus: {
            type: String,
            default: null,
        },
        githubTestMessage: {
            type: String,
            default: '',
        },
    },
    emits: [
        'saveSettings',
        'updateZoomSensitivity',
        'updateConnectionSpacing',
        'testMqttConnection',
        'testHassConnection',
        'testSftpConnection',
        'testGithubConnection',
        'loadSettingsFromFile',
    ],
    methods: {
        handleLoadSettingsFromFile(event) {
            const file = event.target.files[0];
            this.$emit('loadSettingsFromFile', file);
        },
    },
};
</script>
